import React from 'react';
import {
  Editor,
  EditorState,
  convertToRaw,
  getDefaultKeyBinding
} from 'draft-js';
import 'draft-js/dist/Draft.css';
import { colorStyleMap } from './renderUtils';
import ColorControls from './ColorControls';
import { fromDraftJsToJuryBox } from './Transformer';
import {useStyles} from './styles';
import useEmojiSelector from "../../hooks/useEmojiSelector";
import { computeLiveCommentDelta } from '../../collaboration/liveTypingDelta';
import {
  buildEditorStateFromContent,
  reconcileRemoteContent,
} from './editorStateUtils';

const clickFlag = {
  active: false
};

/**
 * Caret rect at the very end of a Draft.js block element. Using a DOM
 * Range gives the exact pixel position of the last visual line, which
 * accounts for proportional fonts and soft-wrapped lines.
 */
function endOfBlockCaretRect(blockEl) {
  try {
    const range = document.createRange();
    range.selectNodeContents(blockEl);
    range.collapse(false);
    const rects = range.getClientRects();
    if (rects.length > 0) {
      return rects[rects.length - 1];
    }
  } catch (e) {
    // fall through to the block bounding box
  }
  return blockEl.getBoundingClientRect();
}

function positionOverlay(overlay, blockEls, layerRect) {
  const blockCount = blockEls.length;
  if (blockCount === 0) {
    return { top: 0, left: 0, maxWidth: layerRect.width };
  }

  // Remote typed new lines past the end of the local text: render the
  // ghost block below the last local line.
  if (overlay.mode === 'append' || overlay.lineIndex >= blockCount) {
    const rect = blockEls[blockCount - 1].getBoundingClientRect();
    const left = rect.left - layerRect.left;
    return {
      top: rect.bottom - layerRect.top,
      left,
      maxWidth: Math.max(40, layerRect.width - left),
    };
  }

  // Remote extended or rewrote an existing line: render the ghost text
  // immediately after the end of that line.
  const blockEl = blockEls[Math.max(0, overlay.lineIndex)];
  const caret = endOfBlockCaretRect(blockEl);
  const gap = overlay.mode === 'inline' ? 2 : 8;
  const left = caret.left - layerRect.left + gap;
  return {
    top: caret.top - layerRect.top,
    left,
    maxWidth: Math.max(40, layerRect.width - left),
  };
}

export default React.forwardRef(({
  label,
  richTextStyles,
  text,
  onSave,
  onLiveChange,
  onFocusField,
  onBlurField,
  remoteTypists = [],
  autoFocus,
  focusOn = null,
  focusAfter = 0,
}, ref) => {

  const classes = useStyles();

  // The editable editor owns its state: it initializes once and receives
  // external changes only through the imperative handle (applyRemoteContent,
  // restoreContent), so the user's cursor survives remote updates.
  const [editorState, setEditorState] = React.useState(() =>
    buildEditorStateFromContent(text, richTextStyles),
  );

  const editor = React.useRef(null);
  const editorStateRef = React.useRef(editorState);
  editorStateRef.current = editorState;
  const didAutoFocusRef = React.useRef(false);

  function markAsClick() {
    clickFlag.active = true;
    setTimeout(() => {
      clickFlag.active = false;
    }, 700);
  }

  function focusEditor() {
    if (editor?.current) {
      editor.current.focus();
    }
  }

  // Place the caret at the end of the notes and focus. Used whenever focus
  // doesn't come from a click on the text itself (autofocus on open, clicks
  // on the padding around the text) — a fresh editor's selection sits at
  // offset 0, so a bare focus() would jump the caret to the start.
  //
  // Order matters: Draft's focus() force-applies the selection from the
  // editor's *current* props, so focusing after setEditorState would clobber
  // the end-selection with the stale offset-0 one. Focus first (its synchronous
  // onChange lands in editorStateRef), then move the selection to the end.
  function focusEditorAtEnd() {
    focusEditor();
    const next = EditorState.moveFocusToEnd(editorStateRef.current);
    editorStateRef.current = next;
    setEditorState(next);
  }

  // Clicks on the text let Draft place the caret natively; clicks on the
  // surrounding empty area mean "continue typing", so go to the end.
  function handleEditorAreaClick(e) {
    const contents = layerRef.current?.querySelector('[data-contents="true"]');
    if (contents && contents.contains(e.target)) {
      return;
    }
    focusEditorAtEnd();
  }

  React.useEffect(() => {
    if (autoFocus && !didAutoFocusRef.current && text !== "") {
      didAutoFocusRef.current = true;
      const active = document.activeElement;
      const isTypingElsewhere = active && (
        active.tagName === 'INPUT' ||
        active.tagName === 'TEXTAREA' ||
        active.isContentEditable
      );
      if (!isTypingElsewhere) {
        setTimeout(() => {
          focusEditorAtEnd();
        }, 0);
      }
    }
  }, [autoFocus, text]);

  function handleFocus(e) {
    if (onFocusField) {
      onFocusField();
    }
  }

  function handleBlur() {
    if (onBlurField) {
      onBlurField();
    }
  }

  function getContent() {
    let raw = convertToRaw(editorStateRef.current.getCurrentContent());
    return fromDraftJsToJuryBox(raw);
  }

  function getPlainText() {
    return editorStateRef.current.getCurrentContent().getPlainText();
  }

  /**
   * Fold a remote save into the editor. Unchanged local lines keep their
   * block instances, so the user's cursor never moves. Returns the
   * resulting plain text synchronously.
   */
  function applyRemoteContent(baseText, remoteText, remoteStyles) {
    const next = reconcileRemoteContent(
      editorStateRef.current,
      baseText,
      remoteText,
      remoteStyles,
    );
    if (next !== editorStateRef.current) {
      editorStateRef.current = next;
      setEditorState(next);
    }
    return next.getCurrentContent().getPlainText();
  }

  function restoreContent(comments, styles) {
    const nextState = EditorState.moveFocusToEnd(
      buildEditorStateFromContent(comments, styles),
    );
    editorStateRef.current = nextState;
    setEditorState(nextState);
  }

  React.useImperativeHandle(ref, () => {
    return {
      getContent,
      getPlainText,
      applyRemoteContent,
      restoreContent,
      focusEditor,
    };
  });

  function keyBindingFn(e) {
    if (onSave && e.shiftKey && e.key === "Enter") {
      return 'rte-save-contents';
    }
    return getDefaultKeyBinding(e);
  }

  function handleKeyCommand(command) {
    if (command === "rte-save-contents" && onSave) {
      onSave();
      return 'handled';
    }
    return 'not-handled'
  }

  function handleEditorChange(nextState) {
    const contentChanged =
      nextState.getCurrentContent() !==
      editorStateRef.current.getCurrentContent();

    // Notify before advancing the ref so undo snapshots capture the
    // pre-change content.
    if (onLiveChange && contentChanged) {
      const content = nextState.getCurrentContent();
      const plainText = content.getPlainText();
      const selection = nextState.getSelection();
      onLiveChange({
        comments: plainText,
        cursor: selection.getAnchorOffset(),
        activeField: 'comments',
      });
    }

    editorStateRef.current = nextState;
    setEditorState(nextState);
  }

  useEmojiSelector(editor, editorState, setEditorState, focusOn, focusAfter);

  const localPlain = editorState.getCurrentContent().getPlainText();

  const liveOverlays = React.useMemo(() => {
    return remoteTypists
      .map((state) => {
        const deltaInfo = computeLiveCommentDelta(localPlain, state.comments);
        if (!deltaInfo?.delta) {
          return null;
        }

        return {
          key: state.sessionId,
          delta: deltaInfo.delta,
          lineIndex: deltaInfo.lineIndex,
          mode: deltaInfo.mode,
          color: state.color,
        };
      })
      .filter(Boolean);
  }, [localPlain, remoteTypists]);

  // Measure overlay positions from the real Draft.js DOM so ghost text
  // lands exactly after the right line (handles proportional fonts and
  // wrapped lines, unlike fixed line-height math).
  const layerRef = React.useRef(null);
  const [overlayPositions, setOverlayPositions] = React.useState({});

  React.useLayoutEffect(() => {
    const layer = layerRef.current;
    if (!layer) {
      return;
    }
    if (liveOverlays.length === 0) {
      setOverlayPositions((prev) =>
        Object.keys(prev).length === 0 ? prev : {},
      );
      return;
    }

    const layerRect = layer.getBoundingClientRect();
    const blockEls = layer.querySelectorAll('[data-contents="true"] > *');
    const next = {};
    for (const overlay of liveOverlays) {
      next[overlay.key] = positionOverlay(overlay, blockEls, layerRect);
    }
    setOverlayPositions((prev) =>
      JSON.stringify(prev) === JSON.stringify(next) ? prev : next,
    );
  }, [liveOverlays, localPlain]);

  return (
    <div className={classes.container}>
      <div className={classes.header}>
        <div className={classes.titleContainer}>
          {label}
        </div>
        <div className={classes.controls}>
          <ColorControls
            editorState={editorState}
            onChange={handleEditorChange}
          />
        </div>
      </div>
      <div className={classes.editor} onMouseDown={markAsClick} onClick={handleEditorAreaClick}>
        <div className={classes.draftLayer} ref={layerRef}>
          <Editor ref={editor}
                  customStyleMap={colorStyleMap}
                  editorState={editorState}
                  onChange={handleEditorChange}
                  onFocus={handleFocus}
                  onBlur={handleBlur}
                  handleKeyCommand={handleKeyCommand}
                  keyBindingFn={keyBindingFn}
          />
          {liveOverlays.map((overlay) => {
            const position = overlayPositions[overlay.key];
            if (!position) {
              return null;
            }

            return (
              <div
                key={overlay.key}
                className={classes.remoteLiveTyping}
                style={{
                  color: overlay.color,
                  top: position.top,
                  left: position.left,
                  maxWidth: position.maxWidth,
                }}
                aria-hidden
              >
                {overlay.delta}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
});
